import { FC, useCallback, useContext, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  AuthenticationContext,
  useUpdateAssociate,
  languageCodes,
  LanguageCode,
  Associate,
} from '@vs-online-only-app/state';
import { ProfileSetupContext } from '../ProfileSetupContext';
import ProfileSetupContinueButton from '../ProfileSetupContinueButton';
import ProfileSetupBackButton from '../ProfileSetupBackButton';
import ProfileSetupError from '../ProfileSetupError';
import LanguageSelector from '../../LanguagesSelector';

const getDefaultLanguages = (user?: Associate) => {
  const spokenLanguages = user?.spokenLanguages;

  if (!spokenLanguages) {
    return languageCodes.filter(({ name }) => name === 'English');
  }

  return languageCodes.filter(({ languageCode }) =>
    spokenLanguages.includes(languageCode),
  );
};

const SelectLanguages: FC = () => {
  const { user, refetchUser } = useContext(AuthenticationContext);

  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [languages, setLanguages] = useState<LanguageCode[]>(
    getDefaultLanguages(user),
  );

  const { goToNextStep } = useContext(ProfileSetupContext);
  const updateAssociate = useUpdateAssociate();

  const handleSubmit = async () => {
    if (!languages.length) {
      setErrorMessage('missing value');
      return;
    }
    const spokenLanguages = languages.map(({ languageCode }) => languageCode);

    setError(false);
    setIsLoading(true);
    setErrorMessage('');

    try {
      await updateAssociate.mutateAsync({ spokenLanguages });
      await refetchUser();
      goToNextStep();
    } catch (e) {
      console.error(e);
      setError(true);
    }

    setIsLoading(false);
  };

  const handleFieldChange = useCallback((newValue: LanguageCode[]) => {
    if (!newValue) {
      setErrorMessage('missing value');
    } else {
      setErrorMessage('');
    }
    setLanguages(newValue);
  }, []);

  const handleFieldBlur = useCallback(() => {
    if (!user) return;

    setErrorMessage(!languages.length ? 'missing value' : '');
  }, [languages, user]);

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <ProfileSetupBackButton />
      <Stack gap={1} alignItems="center">
        <Typography variant="h5" textAlign="center">
          What languages do you speak?
        </Typography>
        <Typography textAlign="center">
          Select all the languages you can speak, customers will see this information.
        </Typography>
      </Stack>
      {error && <ProfileSetupError title="languages" />}
      <LanguageSelector
        label={`Languages${errorMessage ? ` - ${errorMessage}` : ''}`}
        error={!!errorMessage}
        disabled={isLoading}
        onChange={handleFieldChange}
        onBlur={handleFieldBlur}
        languages={languages}
      />

      <ProfileSetupContinueButton
        onClick={handleSubmit}
        loading={isLoading}
        disabled={languages.length === 0}
      >
        Next
      </ProfileSetupContinueButton>
    </Stack>
  );
};

export default SelectLanguages;
